import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { 
  Heart, 
  Stethoscope, 
  Users, 
  Award, 
  Phone, 
  ArrowRight,
  Shield,
  Clock,
  Home as HomeIcon,
  Activity,
  Brain,
  Accessibility,
  CheckCircle2
} from 'lucide-react';

const HERO_VIDEO = "https://customer-assets.emergentagent.com/job_care-solutions-pro/artifacts/f3b2p7li_uhd_25fps.mp4";
const TEAM_IMAGE = "https://customer-assets.emergentagent.com/job_care-solutions-pro/artifacts/qwnqv4z5_pexels-shkrabaanthony-7345460.jpg";

const services = [
  {
    icon: Stethoscope,
    title: 'Skilled Nursing',
    description: 'Comprehensive nursing care including medication management, wound care, and chronic disease management.',
  },
  {
    icon: Activity,
    title: 'Physical Therapy',
    description: 'Restore mobility and strength with personalized rehabilitation programs in your home.',
  },
  {
    icon: Brain,
    title: 'Occupational Therapy',
    description: 'Regain independence in daily activities with specialized therapeutic interventions.',
  },
  {
    icon: Accessibility,
    title: 'Speech Therapy',
    description: 'Expert care for swallowing disorders, language skills, and cognitive rehabilitation.',
  },
  {
    icon: Heart,
    title: 'Cardiac Care',
    description: 'Specialized management for CHF, COPD, and post-cardiac procedure recovery.',
  },
  {
    icon: Shield,
    title: 'Wound Care',
    description: 'Advanced wound care management including ostomy care and surgical wound healing.',
  },
];

const features = [
  {
    icon: Award,
    title: 'Medicare Certified',
    description: 'Fully certified to provide Medicare-covered home health services.',
  },
  {
    icon: Shield,
    title: 'CHAP/ACHC Accredited',
    description: 'Meeting the highest standards of quality and patient safety.',
  },
  {
    icon: Clock,
    title: '24/7 Clinical Support',
    description: 'Round-the-clock access to our clinical team when you need it.',
  },
  {
    icon: HomeIcon,
    title: 'Care at Home',
    description: 'Professional healthcare delivered in the comfort of your home.',
  },
];

const stats = [
  { value: '15+', label: 'Years of Experience' },
  { value: '5,000+', label: 'Patients Served' },
  { value: '24/7', label: 'Clinical Support' },
  { value: '98%', label: 'Patient Satisfaction' },
];

export const Home = () => {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative min-h-[85vh] flex items-center overflow-hidden" data-testid="hero-section">
        {/* Video Background */}
        <div className="absolute inset-0">
          <video
            autoPlay
            muted
            loop
            playsInline
            className="absolute inset-0 w-full h-full object-cover"
          >
            <source src={HERO_VIDEO} type="video/mp4" />
          </video>
          <div className="absolute inset-0 bg-gradient-to-r from-[#000080]/80 to-[#00A878]/60"></div>
        </div>
        
        <div className="container mx-auto px-4 md:px-6 lg:px-8 relative z-10">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm rounded-full px-4 py-2 mb-6 animate-fade-in-up">
              <Shield className="w-5 h-5 text-[#34D399]" />
              <span className="text-white text-sm font-medium">Medicare Certified & CHAP/ACHC Accredited</span>
            </div>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6 animate-fade-in-up animation-delay-100">
              Trust Us for Your Care
            </h1>
            
            <p className="text-xl md:text-2xl text-white/90 mb-4 animate-fade-in-up animation-delay-200">
              "Safely Home" with Senior Care Solutions
            </p>
            
            <p className="text-lg text-white/80 mb-8 max-w-2xl animate-fade-in-up animation-delay-300">
              Navigating clients through the healthcare continuum with compassionate, 
              skilled home healthcare services throughout Florida.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 animate-fade-in-up animation-delay-400">
              <Link to="/contact" data-testid="hero-cta-consultation">
                <Button 
                  size="lg" 
                  className="bg-[#00A878] hover:bg-[#065F46] text-white rounded-full px-8 py-6 text-lg font-semibold shadow-lg shadow-emerald-900/30 transition-all hover:scale-105"
                >
                  Request Free Consultation
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
              <a href="tel:+18773106120" data-testid="hero-cta-call">
                <Button 
                  size="lg" 
                  variant="outline"
                  className="bg-white/10 backdrop-blur-sm border-2 border-white text-white hover:bg-white hover:text-[#000080] rounded-full px-8 py-6 text-lg font-semibold transition-all"
                >
                  <Phone className="w-5 h-5 mr-2" />
                  877.310.6120
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Badges */}
      <section className="bg-white py-10 border-b border-slate-100" data-testid="trust-badges">
        <div className="container mx-auto px-4 md:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <div 
                key={feature.title}
                className="flex items-center gap-4 p-4"
              >
                <div className="w-12 h-12 rounded-full bg-[#00A878] flex items-center justify-center flex-shrink-0">
                  <feature.icon className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="font-semibold text-[#0F172A]">{feature.title}</h3>
                  <p className="text-sm text-[#334155]">{feature.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Overview */}
      <section className="py-20 bg-white" data-testid="services-overview">
        <div className="container mx-auto px-4 md:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="inline-block text-[#00A878] font-semibold mb-4">OUR SERVICES</span>
            <h2 className="text-3xl md:text-4xl font-bold text-[#0F172A] mb-6">
              Comprehensive Home Healthcare Services
            </h2>
            <p className="text-lg text-[#334155]">
              Our team of caring professionals provides expertise in hospital-to-home transitions, 
              recovery, and management of acute & chronic illnesses.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <Card 
                key={service.title}
                className="service-card bg-white border border-slate-100 hover:border-[#00A878]/30"
                data-testid={`service-card-${index}`}
              >
                <CardContent className="p-8">
                  <div className="w-14 h-14 rounded-2xl bg-[#ECFDF5] flex items-center justify-center mb-6 group-hover:bg-[#00A878] transition-colors">
                    <service.icon className="w-7 h-7 text-[#00A878]" />
                  </div>
                  <h3 className="text-xl font-semibold text-[#0F172A] mb-3">{service.title}</h3>
                  <p className="text-[#334155] leading-relaxed">{service.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/services" data-testid="view-all-services">
              <Button 
                size="lg"
                className="bg-[#000080] hover:bg-[#1E3A8A] text-white rounded-full px-8 py-6 text-lg"
              >
                View All Services
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="py-20 bg-white" data-testid="about-preview">
        <div className="container mx-auto px-4 md:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="inline-block text-[#00A878] font-semibold mb-4">ABOUT US</span>
              <h2 className="text-3xl md:text-4xl font-bold text-[#0F172A] mb-6">
                Your Partner for Life
              </h2>
              <p className="text-lg text-[#334155] mb-6 leading-relaxed">
                Senior Care Solutions is a Medicare certified & CHAP/ACHC accredited skilled home 
                healthcare agency. Our team of caring and trusted professionals provide expertise 
                in hospital-to-home transitions, recovery, and the management of acute & chronic 
                illnesses.
              </p>
              <p className="text-lg text-[#334155] mb-8 leading-relaxed">
                We're advocates on a mission to help patients live their very best lives and have 
                a great care experience with exceptional outcomes in the location that suits them 
                best—no matter how their needs may change over time.
              </p>
              
              <div className="space-y-4 mb-8">
                {['Compassionate multi-disciplined professional team', 
                  'Hospital-to-home transition expertise', 
                  'Chronic illness management specialists',
                  'Reducing hospitalization risks'].map((item, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <CheckCircle2 className="w-6 h-6 text-[#00A878]" />
                    <span className="text-[#334155]">{item}</span>
                  </div>
                ))}
              </div>

              <Link to="/about" data-testid="learn-more-about">
                <Button 
                  size="lg"
                  className="bg-[#00A878] hover:bg-[#065F46] text-white rounded-full px-8 py-6"
                >
                  Learn More About Us
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
            </div>

            <div className="relative">
              <img
                src={TEAM_IMAGE}
                alt="Our caring medical team"
                className="rounded-2xl shadow-2xl w-full"
              />
              <div className="absolute -bottom-8 -left-8 bg-[#000080] text-white p-8 rounded-2xl shadow-xl max-w-xs">
                <p className="text-3xl font-bold mb-2">15+ Years</p>
                <p className="text-white/80">of providing exceptional home healthcare in Florida</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-gradient-to-r from-[#000080] to-[#1E3A8A]" data-testid="stats-section">
        <div className="container mx-auto px-4 md:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <p className="text-4xl md:text-5xl font-bold text-[#00A878] mb-2">{stat.value}</p>
                <p className="text-white/80">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[#F8FAFC]" data-testid="cta-section">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-[#0F172A] mb-6">
            Ready to Experience Compassionate Care?
          </h2>
          <p className="text-lg text-[#334155] mb-8 max-w-2xl mx-auto">
            Contact us today for a free consultation. Our caring team is ready to help you 
            or your loved one navigate the path to better health at home.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Link to="/contact" data-testid="cta-contact">
              <Button 
                size="lg" 
                className="bg-[#00A878] hover:bg-[#065F46] text-white rounded-full px-8 py-6 text-lg font-semibold"
              >
                Schedule a Consultation
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
            <a href="tel:+18773106120" data-testid="cta-call">
              <Button 
                size="lg" 
                variant="outline"
                className="border-2 border-[#000080] text-[#000080] hover:bg-[#000080] hover:text-white rounded-full px-8 py-6 text-lg font-semibold"
              >
                <Phone className="w-5 h-5 mr-2" />
                Call 877.310.6120
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
